// "Surprise me" for the meal planner: popular family dinners to suggest when
// nobody can decide, mixed with what this household has planned before
// (src/widgets/kitchen/MealsWidget.tsx). Plain titles, grouped by kind so a
// suggestion can say what sort of night it is.

export type IdeaKind = "comfort" | "quick" | "pasta" | "tex-mex" | "asian" | "grill" | "soup" | "veggie" | "seafood" | "breakfast" | "sheet-pan" | "slow-cooker";

export const IDEA_KINDS: Record<IdeaKind, { label: string; emoji: string }> = {
  comfort: { label: "Comfort food", emoji: "🍲" },
  quick: { label: "Quick weeknight", emoji: "⏱️" },
  pasta: { label: "Pasta night", emoji: "🍝" },
  "tex-mex": { label: "Tex-Mex", emoji: "🌮" },
  asian: { label: "Takeout at home", emoji: "🥡" },
  grill: { label: "Off the grill", emoji: "🔥" },
  soup: { label: "Soup & bread", emoji: "🥣" },
  veggie: { label: "Meatless", emoji: "🥗" },
  seafood: { label: "Seafood", emoji: "🐟" },
  breakfast: { label: "Breakfast for dinner", emoji: "🥞" },
  "sheet-pan": { label: "One pan", emoji: "🍳" },
  "slow-cooker": { label: "Slow cooker", emoji: "🫕" },
};

const RAW: Record<IdeaKind, string[]> = {
  comfort: [
    "Meatloaf and mashed potatoes",
    "Chicken pot pie",
    "Shepherd’s pie",
    "Pot roast with carrots",
    "Chicken and dumplings",
    "Baked mac and cheese",
    "Sloppy joes",
    "Salisbury steak",
    "Beef stroganoff",
    "Chicken-fried steak",
    "Swedish meatballs",
    "Tuna noodle casserole",
    "Chicken enchilada casserole",
    "Stuffed bell peppers",
    "Biscuits and sausage gravy",
    "Chicken parmesan",
    "Country fried chicken",
    "Green chile cheeseburgers",
    "Cottage pie",
    "Pork chops and applesauce",
  ],
  quick: [
    "Grilled cheese and tomato soup",
    "Chicken quesadillas",
    "BLTs",
    "Turkey club sandwiches",
    "French bread pizzas",
    "Chicken Caesar wraps",
    "Hot dogs and baked beans",
    "Philly cheesesteaks",
    "Smash burgers",
    "Chicken tenders and fries",
    "Pita pizzas",
    "Loaded baked potatoes",
    "Nachos supreme",
    "Breakfast burritos",
    "Teriyaki chicken rice bowls",
    "Pesto chicken paninis",
    "Buffalo chicken sliders",
    "Egg fried rice",
    "Tuna melts",
    "Cuban sandwiches",
  ],
  pasta: [
    "Spaghetti and meatballs",
    "Fettuccine Alfredo",
    "Lasagna",
    "Baked ziti",
    "Chicken carbonara",
    "Penne alla vodka",
    "Shrimp scampi",
    "Pasta primavera",
    "Cajun chicken pasta",
    "Tortellini soup",
    "Stuffed shells",
    "Bolognese with rigatoni",
    "Cacio e pepe",
    "Pesto pasta with chicken",
    "Chicken tetrazzini",
    "Lemon garlic orzo with shrimp",
    "Ravioli with brown butter and sage",
    "Pasta e fagioli",
    "Mac and cheese with broccoli",
    "One-pot taco pasta",
  ],
  "tex-mex": [
    "Taco night",
    "Chicken fajitas",
    "Beef enchiladas",
    "Green chile chicken enchiladas",
    "Carnitas tacos",
    "Fish tacos",
    "Burrito bowls",
    "Chicken tortilla soup",
    "Pork green chili",
    "Tamale pie",
    "Steak burritos",
    "Chile rellenos",
    "Chicken tinga tostadas",
    "Taco salad",
    "Huevos rancheros",
    "Barbacoa tacos",
    "Shrimp fajitas",
    "Chicken chimichangas",
    "Black bean quesadillas",
    "Birria tacos",
  ],
  asian: [
    "Orange chicken and rice",
    "Beef and broccoli",
    "Chicken lo mein",
    "Teriyaki salmon",
    "Pad Thai",
    "Chicken tikka masala",
    "Butter chicken",
    "Korean beef bowls",
    "Sweet and sour chicken",
    "General Tso’s chicken",
    "Chicken pho",
    "Ramen night",
    "Kung pao chicken",
    "Pork potstickers",
    "Thai green curry",
    "Bibimbap",
    "Chicken katsu",
    "Sesame noodles",
    "Mongolian beef",
    "Chicken satay with peanut sauce",
  ],
  grill: [
    "Burgers and corn on the cob",
    "Grilled chicken and veggies",
    "Steak and baked potatoes",
    "BBQ ribs",
    "Bratwurst and peppers",
    "Chicken kabobs",
    "Grilled salmon",
    "Pulled pork sandwiches",
    "Carne asada",
    "BBQ chicken drumsticks",
    "Grilled pork tenderloin",
    "Teriyaki chicken skewers",
    "Turkey burgers",
    "Grilled shrimp tacos",
    "Tri-tip with chimichurri",
    "Bison burgers",
    "Lamb chops",
    "Grilled pizza",
    "Hawaiian chicken",
    "Elk steaks",
  ],
  soup: [
    "Chicken noodle soup",
    "Beef stew",
    "Chili and cornbread",
    "Broccoli cheddar soup",
    "Potato leek soup",
    "Minestrone",
    "White chicken chili",
    "French onion soup",
    "Zuppa Toscana",
    "Split pea soup",
    "Clam chowder",
    "Lentil soup",
    "Loaded baked potato soup",
    "Chicken gnocchi soup",
    "Tomato basil soup and grilled cheese",
    "Italian wedding soup",
    "Beef barley soup",
    "Butternut squash soup",
    "Corn chowder",
    "Stuffed pepper soup",
  ],
  veggie: [
    "Veggie stir-fry",
    "Black bean burgers",
    "Eggplant parmesan",
    "Margherita pizza",
    "Spinach and ricotta lasagna",
    "Chickpea curry",
    "Veggie burrito bowls",
    "Caprese paninis",
    "Mushroom risotto",
    "Falafel with tzatziki",
    "Sweet potato and black bean tacos",
    "Vegetable fried rice",
    "Greek salad with pita",
    "Shakshuka",
    "Cheese enchiladas",
    "Pesto gnocchi",
    "Stuffed portobellos",
    "Veggie chili",
    "Spanakopita",
    "Cauliflower tacos",
  ],
  seafood: [
    "Fish and chips",
    "Garlic butter shrimp",
    "Baked cod with lemon",
    "Shrimp and grits",
    "Crab cakes",
    "Blackened tilapia",
    "Salmon burgers",
    "Shrimp fried rice",
    "Coconut shrimp",
    "Trout with almonds",
    "Tuna poke bowls",
    "Honey garlic salmon",
    "Cajun shrimp boil",
    "Lobster rolls",
    "Mahi-mahi tacos",
    "Fish sticks and tater tots",
    "Halibut with mango salsa",
    "Seared scallops",
    "Shrimp po’ boys",
    "Cedar plank salmon",
  ],
  breakfast: [
    "Pancakes and bacon",
    "Waffles and eggs",
    "French toast",
    "Breakfast casserole",
    "Biscuits and gravy",
    "Omelette bar",
    "Breakfast tacos",
    "Chicken and waffles",
    "Dutch baby pancake",
    "Quiche and salad",
    "Eggs Benedict",
    "Hash brown casserole",
    "Crepes",
    "Frittata",
    "Breakfast pizza",
  ],
  "sheet-pan": [
    "Sheet-pan chicken fajitas",
    "Sheet-pan sausage and veggies",
    "Sheet-pan nachos",
    "Sheet-pan gnocchi",
    "Lemon herb roast chicken",
    "Sheet-pan salmon and asparagus",
    "Honey mustard chicken and potatoes",
    "Sheet-pan shrimp boil",
    "Roast chicken thighs and carrots",
    "Sheet-pan pancakes",
    "Pork tenderloin and Brussels sprouts",
    "Sheet-pan quesadillas",
    "Greek chicken and potatoes",
    "Kielbasa and potatoes",
    "Sheet-pan meatballs",
  ],
  "slow-cooker": [
    "Slow-cooker pulled pork",
    "Crock-pot chili",
    "Slow-cooker pot roast",
    "Crock-pot chicken tacos",
    "Slow-cooker beef stew",
    "Crock-pot butter chicken",
    "Slow-cooker chicken tortilla soup",
    "Crock-pot lasagna",
    "Slow-cooker BBQ chicken",
    "Crock-pot Italian beef sandwiches",
    "Slow-cooker green chile pork",
    "Crock-pot chicken and rice",
    "Slow-cooker French dip",
    "Crock-pot meatballs",
    "Slow-cooker Mississippi roast",
  ],
};

export interface DinnerIdea {
  title: string;
  kind: IdeaKind;
}

export const DINNER_IDEAS: DinnerIdea[] = (Object.entries(RAW) as [IdeaKind, string[]][]).flatMap(([kind, titles]) => titles.map((title) => ({ title, kind })));

/** Something the household has planned before (favorites or past menus). */
export interface PastMeal {
  title: string;
  notes: string | null;
  recipeUrl: string | null;
  /** Last planned, YYYY-MM-DD; absent for a favorite never planned. */
  lastDate?: string;
  favorite?: boolean;
}

export type Suggestion =
  | { from: "ours"; meal: PastMeal }
  | { from: "idea"; idea: DinnerIdea };

/**
 * One suggestion. About half come from the household's own favorites and past
 * menus (when it has any), the rest from the ideas above. Anything planned in
 * the last `recentDays` and anything just suggested is skipped.
 */
export function suggestDinner(past: PastMeal[], opts: { today: string; skip: string[]; recentDays?: number; random?: () => number }): Suggestion | null {
  const rnd = opts.random ?? Math.random;
  const cutoff = new Date(Date.parse(`${opts.today}T00:00:00Z`) - (opts.recentDays ?? 7) * 86_400_000).toISOString().slice(0, 10);
  const skip = new Set(opts.skip.map((s) => s.toLowerCase()));
  const ours = past.filter((m) => !skip.has(m.title.toLowerCase()) && !(m.lastDate && m.lastDate >= cutoff));
  const known = new Set(past.map((m) => m.title.toLowerCase()));
  const ideas = DINNER_IDEAS.filter((i) => !skip.has(i.title.toLowerCase()) && !known.has(i.title.toLowerCase()));
  const useOurs = ours.length > 0 && (ideas.length === 0 || rnd() < 0.5);
  if (useOurs) {
    // Favorites come up twice as often as one-off past dinners.
    const weighted = ours.flatMap((m) => (m.favorite ? [m, m] : [m]));
    return { from: "ours", meal: weighted[Math.floor(rnd() * weighted.length)]! };
  }
  if (ideas.length === 0) return null;
  return { from: "idea", idea: ideas[Math.floor(rnd() * ideas.length)]! };
}
